import { z } from 'zod';

import { BoletoDataSchema, DarfTaxLineSchema } from './document-extraction.js';

export const GuiaIssSubtypeEnum = z.enum(['DAMSP', 'DARM', 'DUAM', 'DAM', 'NFS_ISS']);
export const GuiaIcmsSubtypeEnum = z.enum(['GNRE', 'GARE', 'DARE', 'DAE']);
export const GrfSubtypeEnum = z.enum(['GRF', 'GRRF', 'GRDE']);
export const GuiaSubtypeEnum = z.enum([
  'DAMSP',
  'DARM',
  'DUAM',
  'DAM',
  'NFS_ISS',
  'GNRE',
  'GARE',
  'DARE',
  'DAE',
  'GRF',
  'GRRF',
  'GRDE',
]);

export const TaxSlotHintEnum = z.enum([
  'IRRF_RETIDO_SERVICOS_PJ',
  'IRRF_RETIDO_SERVICOS_PF',
  'IRRF_RETIDO_ALUGUEIS',
  'PIS_RETIDO',
  'COFINS_RETIDO',
  'CSLL_RETIDO',
  'INSS_RETIDO_SERVICOS',
  'PIS_FATURAMENTO',
  'COFINS_FATURAMENTO',
  'IRPJ_RECOLHER',
  'CSLL_RECOLHER',
  'IRRF_SALARIOS',
  'INSS_FOLHA',
  'INSS_CONTRIBUINTE_INDIVIDUAL',
  'FGTS',
  'SIMPLES_NACIONAL',
  'ISS_RETIDO',
  'ISS_PROPRIO',
  'ICMS_PROPRIO',
  'ICMS_ST',
]);

export const StatutoryDocumentTypeSchema = z.enum([
  'DARF',
  'GPS',
  'GRF',
  'DAS',
  'GUIA_ISS',
  'GUIA_ICMS',
]);
export type StatutoryDocumentType = z.infer<typeof StatutoryDocumentTypeSchema>;

export interface StatutoryExtractorInput {
  images: { mimeType: string; base64: string }[];
  documentType: StatutoryDocumentType;
  /** Optional document ID for LLM traces and analytics */
  documentId?: string;
  /** Optional additional context from user (e.g. email); only used when present */
  additionalUserContext?: string | null;
  /**
   * Currency to assume when the document states none (ISO 4217).
   *
   * Required so a new caller cannot silently fall back to BRL on a non-Brazilian entity.
   * Callers resolve it from the card purchase currency, the card billing currency, or the
   * entity's functional currency — see `resolveExtractionDefaultCurrency`.
   */
  defaultCurrency: string;
}

export const StatutoryClassificationSchema = z
  .object({
    suggestedArchetype: z.literal('STATUTORY'),
    archetypeConfidence: z.number().min(0).max(100),
    archetypeReasoning: z.string(),
  })
  .strict();

export const StatutoryBillDataSchema = z
  .object({
    issueDate: z.string().nullable(),
    dueDate: z.string(),
    documentNumber: z.string().nullable(),
    description: z.string().min(10),
  })
  .strict();

export const StatutoryPartySchema = z
  .object({
    role: z.enum(['VENDOR', 'CUSTOMER']),
    legalName: z.string(),
    taxId: z.string().nullable(),
    taxIdType: z.string().nullable(),
    taxIdValid: z.boolean().nullable(),
    identificationConfidence: z.number().min(0).max(100),
  })
  .strict();

export const StatutoryLineItemSchema = z
  .object({
    sequenceNumber: z.number(),
    description: z.string(),
    quantity: z.number(),
    unitPrice: z.number(),
    grossValue: z.number(),
    discount: z.number(),
    netValue: z.number(),
  })
  .strict();

export const StatutoryFinancialsSchema = z
  .object({
    grossAmount: z.number().nullable(),
    discounts: z.number(),
    netAmount: z.number(),
    currency: z.string(),
    fineAmount: z.number().nullable(),
    interestAmount: z.number().nullable(),
  })
  .strict();

export const StatutoryDarfTaxLineSchema = DarfTaxLineSchema.extend({
  taxCode: z.string().length(4),
}).strict();

export const StatutoryDarfDataSchema = z
  .object({
    barcode: z.string().nullable(),
    barcodeValid: z.boolean().nullable(),
    taxCode: z.string().length(4).nullable(),
    taxDescription: z.string().max(50).nullable(),
    /**
     * Extracted from DCTFWeb's own contributor classification, not inferred from the tax code.
     * Optional for backward-compatible reads of statutory extractions created before this field.
     */
    isRpaContribuinteIndividual: z.boolean().nullable(),
    taxLines: z.array(StatutoryDarfTaxLineSchema),
    dueDate: z.string().nullable(),
    referenceNumber: z.string().nullable(),
    taxpayerTaxId: z.string().nullable(),
    taxpayerName: z.string().nullable(),
    assessmentPeriod: z.string().nullable(),
    principalAmount: z.number(),
    fineAmount: z.number().nullable(),
    interestAmount: z.number().nullable(),
    totalAmount: z.number(),
  })
  .strict()
  .refine((data) => data.assessmentPeriod != null, {
    message: 'assessmentPeriod must be provided',
  });

export const StatutoryGpsDataSchema = z
  .object({
    barcode: z.string().nullable(),
    paymentCode: z.string().nullable(),
    competencyPeriod: z.string(),
    identifierNumber: z.string().nullable(),
    principalAmount: z.number(),
    otherEntitiesAmount: z.number().nullable(),
    fineAmount: z.number().nullable(),
    interestAmount: z.number().nullable(),
    totalAmount: z.number(),
  })
  .strict();

export const StatutoryGrfDataSchema = z
  .object({
    subtype: GrfSubtypeEnum,
    competencyPeriod: z.string(),
    identifierCode: z.string().nullable(),
    totalAmount: z.number(),
    employeeCount: z.number().nullable(),
    barcode: z.string().nullable(),
  })
  .strict();

export const StatutoryDasDataSchema = z
  .object({
    competencyPeriod: z.string(),
    grossRevenue: z.number().nullable(),
    totalAmount: z.number(),
    barcode: z.string().nullable(),
    dasNumber: z.string().nullable(),
  })
  .strict();

export const StatutoryGuiaIssDataSchema = z
  .object({
    subtype: GuiaIssSubtypeEnum,
    municipality: z.string(),
    municipalityCode: z.string().nullable(),
    /** Explicit RPA contributor classification from the municipal guide; never inferred from ISS retention. */
    isRpaContribuinteIndividual: z.boolean().nullable(),
    /** Monthly YYYY-MM-01, or null when the guide shows only a year-only fiscal year. */
    competencyPeriod: z.string().nullable(),
    serviceCode: z.string().nullable(),
    taxBase: z.number().nullable(),
    taxRate: z.number().nullable(),
    totalAmount: z.number(),
    barcode: z.string().nullable(),
    relatedNfseNumber: z.string().nullable(),
  })
  .strict();

export const StatutoryGuiaIcmsDataSchema = z
  .object({
    subtype: GuiaIcmsSubtypeEnum,
    state: z.string(),
    /** Monthly YYYY-MM-01, or null when the guide shows only a year-only fiscal year. */
    competencyPeriod: z.string().nullable(),
    icmsType: z.enum(['PROPRIO', 'ST', 'DIFAL', 'IMPORTACAO']).nullable(),
    taxBase: z.number().nullable(),
    taxRate: z.number().nullable(),
    totalAmount: z.number(),
    barcode: z.string().nullable(),
    guiaNumber: z.string().nullable(),
  })
  .strict();

export const StatutoryInferredFlagsSchema = z
  .object({
    isRecurring: z.literal(true),
    recurringConfidence: z.number().min(0).max(100),
    recurringReasoning: z.string().nullable(),
    isReimbursement: z.literal(false),
    reimbursementConfidence: z.number().min(0).max(100),
    reimbursementReasoning: z.string().nullable(),
    isAlreadyPaid: z.boolean(),
    alreadyPaidConfidence: z.number().min(0).max(100),
    alreadyPaidReasoning: z.string().nullable(),
    isAutomaticBankPayment: z.literal(false),
    automaticBankPaymentConfidence: z.number().min(0).max(100).nullable(),
    automaticBankPaymentReasoning: z.string().nullable(),
    isUrgent: z.boolean(),
    urgentReasoning: z.string().nullable(),
  })
  .strict();

export const StatutoryCategorySuggestionSchema = z
  .object({
    category: z.string(),
    categoryConfidence: z.number().min(0).max(100),
    subcategory: z.string(),
  })
  .strict();

export const StatutoryValidationResultSchema = z
  .object({
    validationType: z.enum(['CNPJ_FORMAT', 'DARF_BARCODE', 'TOTAL_RECONCILIATION', 'DATE_FORMAT']),
    status: z.enum(['PASS', 'FAIL', 'WARNING']),
    message: z.string(),
    affectedFields: z.array(z.string()),
  })
  .strict();

export const StatutoryQualityFlagSchema = z
  .object({
    flagType: z.string(),
    severity: z.enum(['INFO', 'WARNING', 'ERROR']),
    message: z.string(),
    affectedField: z.string(),
    confidence: z.number().nullable(),
  })
  .strict();

export const StatutoryCommonSchema = z
  .object({
    documentTypeConfidence: z.number().min(0).max(100),
    guiaSubtype: GuiaSubtypeEnum.nullable(),
    suggestedTaxSlot: TaxSlotHintEnum.nullable(),
    suggestedTaxSlotConfidence: z.number().min(0).max(100).nullable(),
    /** Monthly YYYY-MM-01, or null when the guide shows only a year-only fiscal year. */
    competencyDate: z.string().nullable(),
    classification: StatutoryClassificationSchema,
    billData: StatutoryBillDataSchema,
    parties: z
      .object({
        vendor: StatutoryPartySchema,
        customer: StatutoryPartySchema,
      })
      .strict(),
    boletoData: BoletoDataSchema.nullable(),
    lineItems: z.array(StatutoryLineItemSchema).min(1),
    financials: StatutoryFinancialsSchema,
    inferredFlags: StatutoryInferredFlagsSchema,
    categorySuggestion: StatutoryCategorySuggestionSchema,
    validationResults: z.array(StatutoryValidationResultSchema).nullable(),
    qualityFlags: z.array(StatutoryQualityFlagSchema).nullable(),
  })
  .strict();

export const DarfExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('DARF'),
  guiaSubtype: z.null(),
  darfData: StatutoryDarfDataSchema,
});

export const GpsExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('GPS'),
  guiaSubtype: z.null(),
  gpsData: StatutoryGpsDataSchema,
});

export const GrfExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('GRF'),
  guiaSubtype: GrfSubtypeEnum,
  grfData: StatutoryGrfDataSchema,
});

export const DasExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('DAS'),
  guiaSubtype: z.null(),
  dasData: StatutoryDasDataSchema,
});

export const GuiaIssExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('GUIA_ISS'),
  guiaSubtype: GuiaIssSubtypeEnum,
  guiaIssData: StatutoryGuiaIssDataSchema,
});

export const GuiaIcmsExtractionSchema = StatutoryCommonSchema.extend({
  documentType: z.literal('GUIA_ICMS'),
  guiaSubtype: GuiaIcmsSubtypeEnum,
  guiaIcmsData: StatutoryGuiaIcmsDataSchema,
});

export type StatutorySpecificExtraction =
  | z.infer<typeof DarfExtractionSchema>
  | z.infer<typeof GpsExtractionSchema>
  | z.infer<typeof GrfExtractionSchema>
  | z.infer<typeof DasExtractionSchema>
  | z.infer<typeof GuiaIssExtractionSchema>
  | z.infer<typeof GuiaIcmsExtractionSchema>;

export const StatutoryDarfBillDocumentExtractionSchema = DarfExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryGpsBillDocumentExtractionSchema = GpsExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryGrfBillDocumentExtractionSchema = GrfExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryDasBillDocumentExtractionSchema = DasExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryGuiaIssBillDocumentExtractionSchema = GuiaIssExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryGuiaIcmsBillDocumentExtractionSchema = GuiaIcmsExtractionSchema.extend({
  primarySource: z.literal('ai'),
});

export const StatutoryBillDocumentExtractionSchema = z.discriminatedUnion('documentType', [
  StatutoryDarfBillDocumentExtractionSchema,
  StatutoryGpsBillDocumentExtractionSchema,
  StatutoryGrfBillDocumentExtractionSchema,
  StatutoryDasBillDocumentExtractionSchema,
  StatutoryGuiaIssBillDocumentExtractionSchema,
  StatutoryGuiaIcmsBillDocumentExtractionSchema,
]);

export type StatutoryBillDocumentExtraction = z.infer<typeof StatutoryBillDocumentExtractionSchema>;
